/**
 * What an order would cost, worked against the live ladder — in integers.
 *
 * `<PayoutPreview>` renders five numbers and every one of them is money, so
 * invariant 6 applies without exception: shares are `bigint` micro-shares,
 * money is `bigint` micro-USDC, prices are integer cents. The only float in the
 * file is the depth-bar-style percentage nobody here computes, which is to say
 * there is none.
 *
 * ## One book, two outcomes
 *
 * Per D1 and §7.8 the engine keeps **one ladder, on the UP leg**. The identity:
 *
 * ```
 * BUY  UP   @ p¢   ≡   SELL DOWN @ (100 − p)¢    a bid at p on the UP book
 * SELL UP   @ p¢   ≡   BUY  DOWN @ (100 − p)¢    an ask at p on the UP book
 * BUY UP @ p crosses BUY DOWN @ q  whenever  p + q ≥ 100
 * ```
 *
 * So a `BUY UP` takes from the **asks**, cheapest first, and pays the ask price
 * in cents. A `BUY DOWN` takes from the **bids**, *highest* first, and pays
 * `100 − bid` — a higher UP bid is a cheaper DOWN share. Both reduce to one
 * ascending walk over "what this share costs me, in cents", which is the only
 * form the ticket ever needs.
 *
 * ## Why the fee is a range and not a number
 *
 * §7.9: the fee depends on the fill price, and for a marketable order that
 * depends on the book as it is 250 ms later (§7.4) — so a single pre-trade fee
 * is *structurally* unable to be right. The ticket shows
 * `[fee(best_reachable), fee(worst_reachable)]` and the figure it calls reserved
 * is §7.7's: the fee at the reachable price **closest to 50¢**, because
 * `p(1 − p)` is not monotonic and the worst reachable fee is not the fee at the
 * worst reachable price. At launch every rate is `0.0000` and the range collapses
 * to `[0, 0]`, which the preview renders as a plain `Fee $0.00` — through the
 * same code path that will render a real one, never from a constant zero
 * (components.md §2.4).
 */

import {
  ceilDiv,
  feeMicroUsdc,
  payoutMicroUsdc,
  type Outcome,
} from '@/lib/decimal'
import type { BookLevelRow, BookState } from '@/stores/book'

/** The ticket places buys. Closing a position is Phase 8's action, and §7.7's
 *  uncovered-sell lock rule comes with it. `side` still exists on the signed
 *  struct, and `wallet/order.ts` encodes it — this file only quotes buys. */
export type QuoteSide = 'BUY'

export interface QuoteInput {
  outcome: Outcome
  side: QuoteSide
  /** Micro-shares, already on `size_step`. */
  sizeShares: bigint
  /**
   * The user's limit **in their own outcome's terms** (§7.1: `price_cents` is
   * "the user's limit, in their own outcome's terms", never normalised).
   * `null` for a market order, which walks to the end of the price range —
   * there is no collar (`docs/06` §F117).
   */
  limitPriceCents: number | null
  /** `config.fees.taker_fee_rate × 10^4`. */
  takerFeeRate4dp: bigint
  /**
   * A market order's money — `balances.available` — or `null`/omitted for no
   * cap. Since `docs/04` §7.7's budgeted market order (2026-09-30) the venue
   * holds what the account has rather than `size × bound`, pays each share at
   * the price it fills at, and stops where the money runs out; this walks the
   * book the same way, so the preview is what will fill.
   */
  budgetMicroUsdc?: bigint | null
  /**
   * Micro-shares at the front of the order that cost nothing — a sell's covered
   * part, which is signed as its own order and fills first. `0n` if omitted.
   */
  freeShares?: bigint
  /** `size_step`. A take the budget cuts short is floored to it, as the venue's is. */
  sizeStep?: bigint
}

export interface QuoteLevel {
  /** What this level costs the user, per share, in their own outcome's terms. */
  priceCents: number
  /** Micro-shares taken from this level. */
  sizeShares: bigint
}

export interface Quote {
  /** Micro-shares that would fill against resting liquidity right now. */
  filledShares: bigint
  /** `sizeShares − filledShares`. Non-zero means the book or the budget is too thin. */
  unfilledShares: bigint
  /**
   * Why `unfilledShares` is non-zero: the book ran out inside the bound, or the
   * budget did first. `null` when the whole size fills.
   */
  shortBy: 'BOOK' | 'BALANCE' | null
  /** A budget was applied — `QuoteInput.budgetMicroUsdc` was a number. */
  budgeted: boolean
  /**
   * What leaves the balance for `filledShares`: the shares that are not free,
   * at the prices they fill at, plus their fee. Only meaningful when `budgeted`,
   * and then never more than the budget.
   */
  spentMicroUsdc: bigint
  /**
   * What the whole size would cost at the book as it stands, fee included and
   * free shares excluded, if money were no object — up to what the book holds.
   * What an order the budget cannot start "needs".
   */
  wantedMicroUsdc: bigint
  /** Every micro-share at or better than the limit, whatever the order asks for. */
  availableShares: bigint

  /** Micro-USDC for `filledShares`. Rounded **up** — a preview never understates. */
  costMicroUsdc: bigint
  /** One winning share redeems for exactly `1.000000` (D1). */
  payoutMicroUsdc: bigint

  /** The touch, and the deepest level reached. `null` when nothing fills. */
  bestPriceCents: number | null
  worstPriceCents: number | null
  /** Volume-weighted, in cents at 4dp — an average of integers is not one (§7.1). */
  avgPriceCents4dp: bigint | null

  /** `[min, max]` over the reachable prices. Equal at launch, and shown either way. */
  feeMinMicroUsdc: bigint
  feeMaxMicroUsdc: bigint
  /** §7.7's reserve: the fee at the reachable price closest to 50¢. */
  feeReserveMicroUsdc: bigint

  /** `(payout − cost) / cost`, in tenths of a percent. `null` when nothing fills. */
  returnTenths: bigint | null
  /** `(avg − best) / best`, in tenths of a percent. `0n` when one level fills it. */
  slipTenths: bigint

  /** The levels consumed, best first. Empty when nothing is reachable. */
  levels: readonly QuoteLevel[]
  /** The ceiling this walk respected: the limit, or `best + collar` on a market. */
  boundPriceCents: number | null
}

export const EMPTY_QUOTE: Quote = {
  filledShares: 0n,
  unfilledShares: 0n,
  shortBy: null,
  budgeted: false,
  spentMicroUsdc: 0n,
  wantedMicroUsdc: 0n,
  availableShares: 0n,
  costMicroUsdc: 0n,
  payoutMicroUsdc: 0n,
  bestPriceCents: null,
  worstPriceCents: null,
  avgPriceCents4dp: null,
  feeMinMicroUsdc: 0n,
  feeMaxMicroUsdc: 0n,
  feeReserveMicroUsdc: 0n,
  returnTenths: null,
  slipTenths: 0n,
  levels: [],
  boundPriceCents: null,
}

/**
 * The ladder as the user's outcome sees it: cost per share ascending.
 *
 * This is the single place the DOWN transform happens. Doing it per component
 * is how the two ladders end up disagreeing about which side is cheap
 * (`stores/book.ts` makes the same argument about rendering).
 */
export function reachableLevels(book: BookState | null, outcome: Outcome): QuoteLevel[] {
  if (!book) return []
  const rows: readonly BookLevelRow[] = outcome === 'UP' ? book.asks : book.bids
  const out: QuoteLevel[] = []
  for (const row of rows) {
    const priceCents = outcome === 'UP' ? row.price_cents : 100 - row.price_cents
    if (!Number.isInteger(priceCents) || priceCents < 1 || priceCents > 99) continue
    const size = microSharesOf(row.size)
    if (size <= 0n) continue
    out.push({ priceCents, sizeShares: size })
  }
  // `asks` arrive ascending and `bids` descending, so both are already cheapest-
  // first in the user's terms. Sorted anyway: the walk's correctness must not
  // depend on a store's ordering convention holding at the far end of a delta.
  out.sort((a, b) => a.priceCents - b.priceCents)
  return out
}

/**
 * Walk the book and price the order.
 *
 * A limit order is bounded by its own limit. A market order is bounded only by
 * the price range: there is no collar (`docs/06` §F117), so it walks to 99¢ in
 * its own terms, which is also the price `<OrderTicket>` signs it at — the
 * preview and the signature agree about how far it may go.
 */
export function quoteOrder(book: BookState | null, input: QuoteInput): Quote {
  const levels = reachableLevels(book, input.outcome)
  const touch = levels[0]?.priceCents ?? null

  const bound =
    input.limitPriceCents !== null ? input.limitPriceCents
    : touch === null ? null
    : 99

  const budget = input.budgetMicroUsdc ?? null
  const budgeted = budget !== null

  if (bound === null || levels.length === 0 || input.sizeShares <= 0n) {
    const unfilled = max0(input.sizeShares)
    return {
      ...EMPTY_QUOTE,
      unfilledShares: unfilled,
      shortBy: unfilled > 0n ? 'BOOK' : null,
      budgeted,
      boundPriceCents: bound,
    }
  }

  const step = input.sizeStep !== undefined && input.sizeStep > 0n ? input.sizeStep : 1n
  const taken: QuoteLevel[] = []
  let remaining = input.sizeShares
  let available = 0n
  // Micro-USDC × 100, so the division by 100 happens once at the end rather
  // than per level. A partial take at a 6dp level need not be an exact
  // micro-USDC amount, which is why this is not `costMicroUsdc` per level.
  let costHundredths = 0n

  // The budget's walk, beside the book's. `free` is what is left of the covered
  // part; `left` is what is left of the money, `null` with no budget.
  let free = input.freeShares ?? 0n
  let left = budget
  let spent = 0n
  let outOfMoney = false
  // The same walk with no budget, for `wantedMicroUsdc`.
  let wantedLeft = input.sizeShares
  let wantedFree = input.freeShares ?? 0n
  let wanted = 0n

  for (const level of levels) {
    if (level.priceCents > bound) break
    available += level.sizeShares

    if (wantedLeft > 0n) {
      const want = level.sizeShares < wantedLeft ? level.sizeShares : wantedLeft
      const wantFree = want < wantedFree ? want : wantedFree
      wanted += paidCost(want - wantFree, level.priceCents, input.takerFeeRate4dp)
      wantedFree -= wantFree
      wantedLeft -= want
    }

    if (remaining <= 0n || outOfMoney) continue
    let take = level.sizeShares < remaining ? level.sizeShares : remaining
    if (left !== null) {
      const freeTake = take < free ? take : free
      let paid = take - freeTake
      if (paid > 0n) {
        const affordable = affordableAt(left, level.priceCents, input.takerFeeRate4dp, step)
        if (affordable < paid) {
          paid = affordable
          outOfMoney = true
        }
        const cost = paidCost(paid, level.priceCents, input.takerFeeRate4dp)
        left -= cost
        spent += cost
      }
      free -= freeTake
      take = freeTake + paid
    }
    if (take <= 0n) continue
    taken.push({ priceCents: level.priceCents, sizeShares: take })
    costHundredths += take * BigInt(level.priceCents)
    remaining -= take
  }

  const filled = input.sizeShares - remaining
  const best = taken[0]?.priceCents ?? null
  const worst = taken[taken.length - 1]?.priceCents ?? null

  // Rounded up, deliberately: the preview is a promise about what leaves the
  // balance, and a promise that rounds down is one the receipt contradicts.
  const cost = filled > 0n ? ceilDiv(costHundredths, 100n) : 0n
  const payout = payoutMicroUsdc(filled)

  const fees = feeBounds(input.sizeShares, input.takerFeeRate4dp, best, worst)

  return {
    filledShares: filled,
    unfilledShares: remaining,
    shortBy: remaining <= 0n ? null : outOfMoney ? 'BALANCE' : 'BOOK',
    budgeted,
    spentMicroUsdc: spent,
    wantedMicroUsdc: wanted,
    availableShares: available,
    costMicroUsdc: cost,
    payoutMicroUsdc: payout,
    bestPriceCents: best,
    worstPriceCents: worst,
    avgPriceCents4dp: filled > 0n ? roundedDiv(costHundredths * 10_000n, filled) : null,
    ...fees,
    returnTenths: cost > 0n ? roundedDiv((payout - cost) * 1000n, cost) : null,
    slipTenths: slipTenths(costHundredths, filled, best),
    levels: taken,
    boundPriceCents: bound,
  }
}

/**
 * The most micro-shares on `step` that `budget` pays for at `priceCents`, fee
 * included — the venue's `affordableFill` (`matcher/match.go`), in the same
 * order: what the money buys, less the fee's share of each share, floored to
 * the step, then down a step while the rounded-up fee still does not fit.
 *
 * Exported for one-tap trading (`domain/one-tap.ts`), which sizes a preset at
 * the single price it is signed at rather than across a walk.
 */
export function affordableAt(budget: bigint, priceCents: number, rate4dp: bigint, step: bigint): bigint {
  if (budget <= 0n) return 0n
  const cents = BigInt(priceCents)
  let shares = (budget * 100n) / cents
  if (rate4dp > 0n) {
    const feePart = rate4dp * cents * (100n - cents)
    shares = (shares * cents * 1_000_000n) / (cents * 1_000_000n + feePart)
  }
  shares = (shares / step) * step
  while (shares > 0n && paidCost(shares, priceCents, rate4dp) > budget) shares -= step
  return shares
}

/**
 * What a sale of `shares` returns, at the quoted average of the complement
 * ladder it is quoted on.
 *
 * A sell is quoted as a buy of the complement (§7.8), so a share sold at `p`
 * is a share of the complement bought at `100 − p`: proceeds are
 * `size × (100 − avg)`. Rounded **down**, which is the direction a preview of
 * incoming money must round — the mirror of `quoteOrder`'s cost rounding up.
 *
 * `avgCents4dp` is cents at 4dp, so the denominator carries both the ¢→$ and the
 * 4dp scale.
 */
export function saleProceedsMicroUsdc(shares: bigint, avgCents4dp: bigint | null): bigint {
  if (shares <= 0n || avgCents4dp === null) return 0n
  const sellCents4dp = 1_000_000n - avgCents4dp
  if (sellCents4dp <= 0n) return 0n
  return (shares * sellCents4dp) / 1_000_000n
}

/** What `shares` bought at `priceCents` take from the balance: notional, rounded up, and fee. */
function paidCost(shares: bigint, priceCents: number, rate4dp: bigint): bigint {
  if (shares <= 0n) return 0n
  return ceilDiv(shares * BigInt(priceCents), 100n) + feeMicroUsdc(shares, rate4dp, priceCents)
}

/**
 * The three fee figures of §7.7 and §7.9.
 *
 * `p(1 − p)` peaks at 50¢, so over a contiguous reachable range `[best, worst]`
 * the largest fee is at whichever endpoint — or interior point — is nearest 50.
 * That is §7.7's reserve price, and it is not the worst *price*: the worked
 * example there reserves at 55¢ against a 58¢ limit for exactly this reason.
 */
function feeBounds(
  sizeShares: bigint,
  rate4dp: bigint,
  bestCents: number | null,
  worstCents: number | null,
): { feeMinMicroUsdc: bigint; feeMaxMicroUsdc: bigint; feeReserveMicroUsdc: bigint } {
  if (bestCents === null || worstCents === null || sizeShares <= 0n) {
    return { feeMinMicroUsdc: 0n, feeMaxMicroUsdc: 0n, feeReserveMicroUsdc: 0n }
  }
  const low = Math.min(bestCents, worstCents)
  const high = Math.max(bestCents, worstCents)
  // The reachable price closest to 50¢ — clamped into the range, so a range
  // entirely above or below 50 picks its nearest endpoint.
  const nearest50 = Math.min(high, Math.max(low, 50))

  const atLow = feeMicroUsdc(sizeShares, rate4dp, low)
  const atHigh = feeMicroUsdc(sizeShares, rate4dp, high)
  const reserve = feeMicroUsdc(sizeShares, rate4dp, nearest50)

  const min = atLow < atHigh ? atLow : atHigh
  return { feeMinMicroUsdc: min, feeMaxMicroUsdc: reserve, feeReserveMicroUsdc: reserve }
}

/** `(avg − best) / best` in tenths of a percent, from the exact accumulator. */
function slipTenths(costHundredths: bigint, filled: bigint, bestCents: number | null): bigint {
  if (filled <= 0n || bestCents === null || bestCents <= 0) return 0n
  const bestHundredths = filled * BigInt(bestCents)
  if (costHundredths <= bestHundredths) return 0n
  return roundedDiv((costHundredths - bestHundredths) * 1000n, bestHundredths)
}

/** Half-up on a non-negative denominator. Local because `lib/decimal` exports
 *  only the ceiling form, which the fee needs and an average must not use. */
function roundedDiv(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) return 0n
  const negative = numerator < 0n
  const abs = negative ? -numerator : numerator
  const q = (abs * 2n + denominator) / (denominator * 2n)
  return negative ? -q : q
}

function max0(value: bigint): bigint {
  return value > 0n ? value : 0n
}

/**
 * A 6dp share string → micro-shares, without `parseFixed`'s throw.
 *
 * A malformed level is dropped rather than fatal: the ladder is a live stream
 * and one bad row must not take the ticket down with it.
 */
function microSharesOf(value: string): bigint {
  const match = /^(\d+)(?:\.(\d{1,6}))?$/.exec(value)
  if (!match?.[1]) return 0n
  return BigInt(match[1]) * 1_000_000n + BigInt((match[2] ?? '').padEnd(6, '0'))
}
